/**
 * Device signals for the app lock (S03): the Idle Detection API (Chromium) and the local vikod's
 * encrypted `machine.presence` report. Both can only make Viko lock; unlocking always needs the
 * passkey ceremony in idle-lock.ts.
 */
import { type MachinePresenceFrame, SYSTEM_IDLE_LOCK_MS } from '@viko/protocol'
import { idb } from './idb.js'
import type { LockSignal } from './idle-lock.js'

export type IdlePermission = 'unsupported' | 'granted' | 'denied' | 'prompt'
interface IdleDetectorLike extends EventTarget {
  userState: 'active' | 'idle' | null
  screenState: 'locked' | 'unlocked' | null
  start(o: { threshold: number; signal?: AbortSignal }): Promise<void>
}
interface IdleDetectorCtor {
  new (): IdleDetectorLike
  requestPermission(): Promise<'granted' | 'denied'>
}
const ctor = () =>
  (globalThis as { IdleDetector?: IdleDetectorCtor }).IdleDetector as IdleDetectorCtor | undefined

export async function idlePermission(): Promise<IdlePermission> {
  if (!ctor()) return 'unsupported'
  try {
    const status = await globalThis.navigator?.permissions?.query({
      name: 'idle-detection' as PermissionName,
    })
    return status?.state ?? 'prompt'
  } catch {
    return 'prompt'
  }
}

/** Needs a user gesture (the one-time, explained prompt or You › Security). */
export async function requestIdlePermission(): Promise<IdlePermission> {
  const IdleDetector = ctor()
  if (!IdleDetector) return 'unsupported'
  try {
    return await IdleDetector.requestPermission()
  } catch {
    return 'denied'
  }
}

/**
 * Start the detector with the system-idle threshold. Resolves false when unsupported or not
 * permitted (usage continues on the fallback timers).
 */
export async function startIdleDetector(
  onSignal: (s: LockSignal) => void,
  o: { signal?: AbortSignal; now?: () => number } = {},
): Promise<boolean> {
  const IdleDetector = ctor()
  if (!IdleDetector) return false
  const now = o.now ?? Date.now
  try {
    const d = new IdleDetector()
    d.addEventListener('change', () => {
      const t = now()
      onSignal({
        available: true,
        screen: d.screenState ?? 'unknown',
        ...(d.screenState === 'locked' ? { lockedAt: t } : {}),
        ...(d.userState === 'idle' ? { idleAt: t } : {}),
      })
    })
    await d.start({ threshold: SYSTEM_IDLE_LOCK_MS, ...(o.signal ? { signal: o.signal } : {}) })
    onSignal({ available: true })
    o.signal?.addEventListener('abort', () => onSignal({ available: false }))
    return true
  } catch {
    onSignal({ available: false })
    return false
  }
}

/** Heartbeat is 30 min on the daemon; older reports mean the signal is not live. */
export const PRESENCE_STALE_MS = 45 * 60_000
export function presenceSignal(
  frame: MachinePresenceFrame | undefined,
  now = Date.now(),
): LockSignal {
  if (!frame) return { available: false }
  return {
    available: frame.screen !== 'unknown' && now - frame.ts < PRESENCE_STALE_MS,
    screen: frame.screen,
    ...(frame.lockedAt !== undefined ? { lockedAt: frame.lockedAt } : {}),
    ...(frame.idleAt !== undefined ? { idleAt: frame.idleAt } : {}),
  }
}

const PRESENCE_REFRESH_MS = 5 * 60_000
export const PRESENCE_THROTTLE_MS = 2_000
/**
 * Read this computer's presence report now, on machine report broadcasts, and periodically.
 *
 * burst-guard (`throttleMs`, default 2 s; 0 = legacy): broadcast-driven refreshes coalesce to a
 * leading read plus at most one trailing read per window (the read shares the report list the
 * machines refetch just fetched). While the page is hidden nothing is read; the first refresh
 * after it becomes visible again runs at once, so a screen lock that happened meanwhile still
 * locks before anyone can use the page.
 */
export function watchPresence(o: {
  machineId: string
  read: (machineId: string) => Promise<MachinePresenceFrame | undefined>
  onWork: (cb: (f: { entity: string; id: string }) => void) => () => void
  onSignal: (s: LockSignal) => void
  now?: () => number
  throttleMs?: number
  hidden?: () => boolean
}): () => void {
  const throttleMs = o.throttleMs ?? PRESENCE_THROTTLE_MS
  const legacy = throttleMs <= 0
  const hidden =
    o.hidden ?? (() => typeof document !== 'undefined' && document.visibilityState === 'hidden')
  let stopped = false
  let running = false
  let again = false
  let missed = false
  let lastWork = Number.NEGATIVE_INFINITY
  let trailing: ReturnType<typeof setTimeout> | undefined
  const refresh = async () => {
    if (!legacy && hidden()) {
      missed = true
      return
    }
    if (running) {
      again = true
      return
    }
    running = true
    try {
      do {
        again = false
        const frame = await o.read(o.machineId).catch(() => undefined)
        if (!stopped) o.onSignal(presenceSignal(frame, (o.now ?? Date.now)()))
      } while (again && !stopped)
    } finally {
      running = false
    }
  }
  const onWorkFrame = () => {
    if (legacy) return void refresh()
    if (trailing) return
    const wait = lastWork + throttleMs - Date.now()
    if (wait <= 0) {
      lastWork = Date.now()
      return void refresh()
    }
    trailing = setTimeout(() => {
      trailing = undefined
      if (stopped) return
      lastWork = Date.now()
      void refresh()
    }, wait)
  }
  const onVisible = () => {
    if (!hidden() && missed) {
      missed = false
      void refresh()
    }
  }
  void refresh()
  const off = o.onWork((f) => {
    if (f.entity === 'machine') onWorkFrame()
  })
  const timer = setInterval(() => void refresh(), PRESENCE_REFRESH_MS)
  const watchVisibility = !legacy && typeof document !== 'undefined'
  if (watchVisibility) document.addEventListener('visibilitychange', onVisible)
  return () => {
    stopped = true
    off()
    clearInterval(timer)
    if (trailing) clearTimeout(trailing)
    if (watchVisibility) document.removeEventListener('visibilitychange', onVisible)
    o.onSignal({ available: false })
  }
}

/** The enrolled machine this browser runs on (set by a local enrollment, or in You › Security). */
const LOCAL_MACHINE_KEY = 'app-lock/local-machine/v1'
export const loadLocalMachine = () =>
  idb.get<string>(LOCAL_MACHINE_KEY).catch(() => undefined) as Promise<string | undefined>
export const saveLocalMachine = (machineId: string | undefined) =>
  machineId === undefined ? idb.del(LOCAL_MACHINE_KEY) : idb.set(LOCAL_MACHINE_KEY, machineId)

/** The one-time Idle Detection explanation: shown once, never again after an answer. */
const IDLE_PROMPT_KEY = 'app-lock/idle-prompt/v1'
export type IdlePromptAnswer = 'granted' | 'denied' | 'dismissed'
export const loadIdlePrompt = () =>
  idb.get<IdlePromptAnswer>(IDLE_PROMPT_KEY).catch(() => undefined) as Promise<
    IdlePromptAnswer | undefined
  >
export const saveIdlePrompt = (a: IdlePromptAnswer) => idb.set(IDLE_PROMPT_KEY, a)
